import { useState } from 'react';
import {
  Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Chip, Divider, Typography, Box, Snackbar,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { ticketsApi, type ServiceTicket } from '../../api/amc.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const CATEGORIES = ['hardware', 'software', 'network', 'civil', 'electrical', 'communication', 'preventive_maintenance', 'emergency_breakdown'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];
const SOURCES = ['portal', 'email', 'call_center', 'mobile_app', 'auto_monitoring'];
const ACTIONS = ['accept', 'on_site', 'pending_spare', 'resolve', 'close', 'cancel', 'reopen'];
const fmtMins = (m?: number | null) => (m == null ? '—' : m < 0 ? `${Math.abs(m)}m over` : `${m}m left`);

export function TicketsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [form, setForm] = useState({ assetId: '', contractId: '', title: '', description: '', category: 'hardware', priority: 'medium', source: 'portal' });
  const [actionForm, setActionForm] = useState({ action: 'accept', remarks: '', rootCause: '', correctiveAction: '', downtimeMinutes: '', firstTimeFix: false });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['service-tickets', page, limit, statusFilter], queryFn: () => ticketsApi.list({ page, limit, status: statusFilter || undefined }) });
  const detail = useQuery({ queryKey: ['service-ticket', detailId], queryFn: () => ticketsApi.detail(detailId as number), enabled: detailId !== null });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['service-tickets'] }); qc.invalidateQueries({ queryKey: ['service-ticket'] }); };

  const create = useMutation({
    mutationFn: () => ticketsApi.create({ assetId: form.assetId ? Number(form.assetId) : undefined, contractId: form.contractId ? Number(form.contractId) : undefined, title: form.title, description: form.description || undefined, category: form.category, priority: form.priority, source: form.source }),
    onSuccess: () => { setOpen(false); invalidate(); }, onError: (e) => setError(extractApiError(e)),
  });
  const dispatch = useMutation({ mutationFn: (id: number) => ticketsApi.dispatch(id), onSuccess: () => { setToast('Technician auto-dispatched'); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });
  const act = useMutation({
    mutationFn: () => {
      const body: { action: string; remarks?: string; firstTimeFix?: boolean; breakdown?: Record<string, unknown> } = { action: actionForm.action, remarks: actionForm.remarks || undefined };
      if (actionForm.action === 'resolve') {
        body.firstTimeFix = actionForm.firstTimeFix;
        body.breakdown = { rootCause: actionForm.rootCause || undefined, correctiveAction: actionForm.correctiveAction || undefined, downtimeMinutes: actionForm.downtimeMinutes ? Number(actionForm.downtimeMinutes) : undefined };
      }
      return ticketsApi.action(detailId as number, body);
    },
    onSuccess: () => { setToast('Ticket updated'); invalidate(); }, onError: (e) => setToast(extractApiError(e)),
  });

  const columns: Column<ServiceTicket>[] = [
    { key: 'code', label: 'Code', width: 140 },
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category', render: (r) => r.category.replace(/_/g, ' ') },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" color={r.priority === 'critical' ? 'error' : r.priority === 'high' ? 'warning' : 'default'} label={r.priority} variant="outlined" /> },
    { key: 'sla', label: 'Resolution SLA', render: (r) => <Typography variant="caption" color={r.resolutionBreached ? 'error.main' : 'text.secondary'}>{r.resolutionBreached ? 'BREACHED' : fmtMins(r.resolutionRemainingMins)}</Typography> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const d = detail.data;

  return (
    <>
      <PageHeader title="Service Tickets" subtitle="Complaint lifecycle, SLA tracking, technician dispatch and breakdown capture." action={hasPermission('ticket.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>New Ticket</Button>} />

      <TableToolbar filters={
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} sx={{ width: 200 }}>
          <MenuItem value="">All</MenuItem>
          {['open', 'assigned', 'accepted', 'on_site', 'pending_spare', 'resolved', 'closed', 'cancelled'].map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      } />
      <DataTable columns={columns} rows={list.data?.data ?? []} loading={list.isLoading} page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id} emptyTitle="No tickets"
        actions={(r) => <Button size="small" onClick={() => { setDetailId(r.id); setActionForm({ action: 'accept', remarks: '', rootCause: '', correctiveAction: '', downtimeMinutes: '', firstTimeFix: false }); }}>Open</Button>}
      />

      {/* Create dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Service Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={2} />
            <Stack direction="row" spacing={2}>
              <TextField label="Asset ID" type="number" value={form.assetId} onChange={(e) => setForm((f) => ({ ...f, assetId: e.target.value }))} fullWidth />
              <TextField label="Contract ID" type="number" value={form.contractId} onChange={(e) => setForm((f) => ({ ...f, contractId: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth>{CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}</TextField>
              <TextField select label="Priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} fullWidth>{PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField>
              <TextField select label="Source" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} fullWidth>{SOURCES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}</TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.title} onClick={() => create.mutate()}>Create</Button></DialogActions>
      </Dialog>

      {/* Detail / workflow dialog */}
      <Dialog open={detailId !== null} onClose={() => setDetailId(null)} fullWidth maxWidth="sm">
        <DialogTitle>{d?.code ?? 'Ticket'} {d && <StatusBadge status={d.status} />}</DialogTitle>
        <DialogContent>
          {d && (
            <Stack spacing={1.5} mt={1}>
              <Typography variant="subtitle1">{d.title}</Typography>
              <Stack direction="row" spacing={1}><Chip size="small" label={d.category.replace(/_/g, ' ')} /><Chip size="small" label={d.priority} color={d.priority === 'critical' ? 'error' : 'default'} /><Chip size="small" label={d.source.replace(/_/g, ' ')} variant="outlined" /></Stack>
              <Typography variant="caption" color={d.resolutionBreached ? 'error.main' : 'text.secondary'}>Resolution SLA: {d.resolutionBreached ? 'BREACHED' : fmtMins(d.resolutionRemainingMins)} · Response: {d.responseBreached ? 'BREACHED' : fmtMins(d.responseRemainingMins)}</Typography>
              <Typography variant="caption">Assigned technician: {d.assignedTechnicianId ?? '—'}</Typography>
              {d.breakdown && <Alert severity="info">Root cause: {String((d.breakdown as Record<string, unknown>).root_cause ?? '—')} · Downtime: {String((d.breakdown as Record<string, unknown>).downtime_minutes ?? '—')}m</Alert>}

              {hasPermission('ticket.assign') && ['open', 'assigned'].includes(d.status) && (
                <Button size="small" variant="outlined" onClick={() => dispatch.mutate(d.id)}>Auto-dispatch technician</Button>
              )}

              <Divider />
              {hasPermission('ticket.update') && !['closed', 'cancelled'].includes(d.status) && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Lifecycle action</Typography>
                  <Stack spacing={1.5}>
                    <TextField select size="small" label="Action" value={actionForm.action} onChange={(e) => setActionForm((f) => ({ ...f, action: e.target.value }))}>
                      {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a.replace(/_/g, ' ')}</MenuItem>)}
                    </TextField>
                    {actionForm.action === 'resolve' && (
                      <>
                        <TextField select size="small" label="First time fix?" value={actionForm.firstTimeFix ? 'yes' : 'no'} onChange={(e) => setActionForm((f) => ({ ...f, firstTimeFix: e.target.value === 'yes' }))}>
                          <MenuItem value="yes">Yes</MenuItem><MenuItem value="no">No</MenuItem>
                        </TextField>
                        <TextField size="small" label="Root cause" value={actionForm.rootCause} onChange={(e) => setActionForm((f) => ({ ...f, rootCause: e.target.value }))} />
                        <TextField size="small" label="Corrective action" value={actionForm.correctiveAction} onChange={(e) => setActionForm((f) => ({ ...f, correctiveAction: e.target.value }))} />
                        <TextField size="small" label="Downtime (minutes)" type="number" value={actionForm.downtimeMinutes} onChange={(e) => setActionForm((f) => ({ ...f, downtimeMinutes: e.target.value }))} />
                      </>
                    )}
                    <TextField size="small" label="Remarks" value={actionForm.remarks} onChange={(e) => setActionForm((f) => ({ ...f, remarks: e.target.value }))} />
                    <Button variant="contained" size="small" onClick={() => act.mutate()}>Apply</Button>
                  </Stack>
                </Box>
              )}

              <Divider />
              <Typography variant="subtitle2">History</Typography>
              <Stack spacing={0.5}>
                {(d.history ?? []).map((h, i) => <Typography key={i} variant="caption" color="text.secondary">{h.action} → {h.to_status} · {new Date(h.created_at).toLocaleString()}</Typography>)}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailId(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
